/**
 * Documents Bulk Upload 탭 — 운영자가 서비스 대시보드에서 N 개 마크다운/플레인텍스트를
 * 한 번에 업로드한다.
 *
 * 흐름:
 * 1. 다중 파일 선택 (`<input type="file" multiple>` 또는 드래그앤드롭)
 * 2. 클라이언트가 파일을 base64 인코드 + items[] 구성
 * 3. dryRun=true 로 호출 → 토큰/비용 추정 표시 + 사용자 confirm 게이트
 * 4. 확인 후 dryRun=false 로 실 업로드 → items[].ok 별 progress + 부분 실패 표시
 *
 * 운영 가이드:
 * - 1 batch 최대 50 문서 (Phase 1 동기 처리 한도)
 * - 본문 1MB/문서 한도 (`DOCUMENT_BODY_MAX_BYTES`)
 * - markdown / plaintext 만 (PDF/docx 는 v2 후속)
 *
 * 접근성 (Red team R9 round1):
 * - 드롭존 `role="button"` + `tabIndex={0}` + `aria-label` + 키보드 Enter/Space 열기
 * - busy 상태 `aria-busy` 부착
 * - 결과 패널 `role="status"` + `aria-live="polite"`
 * - 모든 색은 `src/styles/tokens.css` 의 `--pk-success/danger/warning-*` 토큰 사용 (raw hex 금지)
 */

import { useCallback, useMemo, useRef, useState } from "react"
import type { AdminClient, BulkUploadDocumentsResponse } from "../../../api/admin-client.ts"

type FileItem = {
	readonly file: File
	readonly documentId: string
	readonly contentType: string
	readonly summary: string
}

const MAX_ITEMS = 50
const MAX_BYTES = 1024 * 1024

function inferDocumentId(fileName: string): string {
	const base = fileName.replace(/\.(md|markdown|txt)$/i, "")
	return (
		base
			.toLowerCase()
			.replace(/[^a-z0-9-]+/g, "-")
			.replace(/^-+|-+$/g, "")
			.slice(0, 128) || "doc"
	)
}

function inferContentType(fileName: string): string {
	if (/\b(runbook|incident|playbook)\b/i.test(fileName)) return "runbook"
	if (/\b(adr|decision)\b/i.test(fileName)) return "adr"
	if (/\b(meeting|notes)\b/i.test(fileName)) return "meeting"
	return "markdown"
}

/**
 * FileReader 기반 base64 변환 — 큰 파일에서도 V8 argument count 한도 안전.
 * `readFileAsDataURL` 결과 `data:<mime>;base64,<payload>` 에서 payload 만 잘라 반환.
 *
 * 이전 구현은 `String.fromCharCode(...chunk)` spread 사용 — 32K chunk 라 안전했지만
 * 1MB+ 미래 chunk size 변경 시 silent overflow 가능 (R9 round1 권고).
 */
async function readFileAsBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const fr = new FileReader()
		fr.onload = () => {
			const result = typeof fr.result === "string" ? fr.result : ""
			const idx = result.indexOf("base64,")
			resolve(idx >= 0 ? result.slice(idx + "base64,".length) : "")
		}
		fr.onerror = () => reject(fr.error ?? new Error("file read failed"))
		fr.readAsDataURL(file)
	})
}

export function DocumentsBulkUploadTab(props: {
	readonly client: AdminClient
	readonly serviceSlug: string
}) {
	const { client, serviceSlug } = props
	const [items, setItems] = useState<ReadonlyArray<FileItem>>([])
	const [dragOver, setDragOver] = useState(false)
	const [dryRunResult, setDryRunResult] = useState<BulkUploadDocumentsResponse | undefined>()
	const [uploadResult, setUploadResult] = useState<BulkUploadDocumentsResponse | undefined>()
	const [busy, setBusy] = useState<"" | "dry-run" | "upload">("")
	const [error, setError] = useState<string | undefined>()
	// 숨겨진 file input 을 드롭존 키보드 활성화 시 트리거 (R9 a11y)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const totalBytes = useMemo(() => items.reduce((s, i) => s + i.file.size, 0), [items])
	const oversize = useMemo(() => items.filter((i) => i.file.size > MAX_BYTES), [items])

	const addFiles = useCallback(
		(files: FileList | File[]) => {
			setError(undefined)
			setDryRunResult(undefined)
			setUploadResult(undefined)
			const next: FileItem[] = []
			const seen = new Set<string>(items.map((i) => i.documentId))
			for (const file of Array.from(files)) {
				if (items.length + next.length >= MAX_ITEMS) {
					setError(`최대 ${MAX_ITEMS}개까지만 한 번에 업로드 가능합니다.`)
					break
				}
				if (!/\.(md|markdown|txt)$/i.test(file.name)) {
					setError(`지원 형식이 아닙니다 — .md / .markdown / .txt (${file.name})`)
					continue
				}
				let id = inferDocumentId(file.name)
				let n = 1
				while (seen.has(id)) {
					id = `${inferDocumentId(file.name)}-${++n}`
				}
				seen.add(id)
				next.push({
					file,
					documentId: id,
					contentType: inferContentType(file.name),
					summary: file.name
				})
			}
			setItems([...items, ...next])
		},
		[items]
	)

	const removeItem = (idx: number) => {
		setItems(items.filter((_, i) => i !== idx))
		setDryRunResult(undefined)
		setUploadResult(undefined)
	}

	const editItem = (idx: number, patch: Partial<FileItem>) => {
		setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
	}

	const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
		e.preventDefault()
		setDragOver(false)
		addFiles(e.dataTransfer.files)
	}

	/** 드롭존 키보드 활성화 — Enter / Space 로 file picker 열기 (R9 a11y). */
	const onDropZoneKey: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault()
			fileInputRef.current?.click()
		}
	}

	const buildItemsPayload = useCallback(async () => {
		const out: Array<{
			documentId: string
			mimeType: "text/markdown" | "text/x-markdown" | "text/plain"
			bodyBase64: string
			summary: string
			contentType: string
		}> = []
		for (const it of items) {
			const isMarkdown = /\.(md|markdown)$/i.test(it.file.name)
			const bodyBase64 = await readFileAsBase64(it.file)
			out.push({
				documentId: it.documentId,
				mimeType: isMarkdown ? "text/markdown" : "text/plain",
				bodyBase64,
				summary: it.summary,
				contentType: it.contentType
			})
		}
		return out
	}, [items])

	const runDryRun = async () => {
		setBusy("dry-run")
		setError(undefined)
		try {
			const payload = await buildItemsPayload()
			const res = await client.bulkUploadDocuments(serviceSlug, { items: payload, dryRun: true })
			setDryRunResult(res)
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e))
		} finally {
			setBusy("")
		}
	}

	const runUpload = async () => {
		setBusy("upload")
		setError(undefined)
		try {
			const payload = await buildItemsPayload()
			const res = await client.bulkUploadDocuments(serviceSlug, { items: payload, dryRun: false })
			setUploadResult(res)
			setDryRunResult(undefined)
			// 성공 항목은 목록에서 제거 (재업로드 방지)
			const failedIds = new Set(res.items.filter((r) => !r.ok).map((r) => r.documentId))
			setItems((curr) => curr.filter((it) => failedIds.has(it.documentId)))
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e))
		} finally {
			setBusy("")
		}
	}

	const isBusy = busy !== ""
	const dropZoneAriaLabel = `문서 업로드 영역 — Enter 또는 Space 키로 파일 선택 다이얼로그 열기. 현재 ${items.length}개 / 최대 ${MAX_ITEMS}개.`

	return (
		<div className="card" style={{ padding: 24 }} aria-busy={isBusy}>
			<h2 style={{ marginTop: 0 }}>Documents Bulk Upload</h2>
			<p className="muted" style={{ marginBottom: 16 }}>
				최대 <strong>{MAX_ITEMS}개</strong> 마크다운/플레인텍스트를 한 번에 업로드합니다. 본문 1MB/문서 한도.
				임베딩은 단일 batch API 호출로 묶여 RPM/비용이 절감됩니다. PDF/docx 는 v2 후속.
			</p>
			<p
				className="muted"
				style={{
					marginBottom: 16,
					padding: 12,
					background: "var(--pk-warning-soft)",
					border: "1px solid var(--pk-warning-border)",
					borderRadius: 6,
					fontSize: 13
				}}
				role="note"
			>
				⚠️ 업로드 본문은 외부 AI Gateway (OpenAI) 로 임베딩 송신됩니다. <strong>PII / 영업기밀</strong>이
				포함된 문서는 사전 마스킹 후 업로드해 주세요. 임베딩 벡터로도 부분 inversion 가능합니다.
			</p>

			{/* biome-ignore lint/a11y/useSemanticElements: <button> 안에 <input type="file"> 을 못 넣어 div + role="button" 필요. */}
			<div
				role="button"
				tabIndex={0}
				aria-label={dropZoneAriaLabel}
				onDragOver={(e) => {
					e.preventDefault()
					setDragOver(true)
				}}
				onDragLeave={() => setDragOver(false)}
				onDrop={onDrop}
				onClick={() => fileInputRef.current?.click()}
				onKeyDown={onDropZoneKey}
				style={{
					padding: 32,
					border: `2px dashed ${dragOver ? "var(--pk-brand-500)" : "var(--pk-gray-300)"}`,
					borderRadius: 8,
					background: dragOver ? "var(--pk-brand-50)" : "transparent",
					textAlign: "center",
					marginBottom: 16,
					cursor: "pointer",
					outline: "none"
				}}
			>
				<p style={{ margin: 0 }}>
					파일을 드래그하거나{" "}
					<span style={{ color: "var(--pk-brand-600)", textDecoration: "underline" }}>클릭해서 선택</span>
				</p>
				<input
					ref={fileInputRef}
					type="file"
					multiple
					accept=".md,.markdown,.txt"
					style={{ display: "none" }}
					onChange={(e) => {
						if (e.target.files) addFiles(e.target.files)
						e.target.value = ""
					}}
				/>
				<p className="muted" style={{ margin: "8px 0 0", fontSize: 13 }}>
					.md / .markdown / .txt — 현재 {items.length} / {MAX_ITEMS}, 총 {(totalBytes / 1024).toFixed(1)} KB
				</p>
			</div>

			{oversize.length > 0 && (
				<div
					style={{
						padding: 12,
						marginBottom: 12,
						background: "var(--pk-danger-soft)",
						border: "1px solid var(--pk-danger-border)",
						borderRadius: 6,
						color: "var(--pk-danger)"
					}}
					role="alert"
				>
					<strong>경고:</strong> {oversize.length}개 파일이 1MB 초과 — 업로드 시 거부됩니다 (
					{oversize.map((i) => i.file.name).join(", ")})
				</div>
			)}

			{items.length > 0 && (
				<div style={{ marginBottom: 16, overflowX: "auto" }}>
					<table style={{ width: "100%", borderCollapse: "collapse" }}>
						<caption className="sr-only">업로드 후보 문서 목록</caption>
						<thead>
							<tr style={{ borderBottom: "1px solid var(--pk-gray-200)" }}>
								<th scope="col" style={{ textAlign: "left", padding: 8 }}>
									파일
								</th>
								<th scope="col" style={{ textAlign: "left", padding: 8 }}>
									documentId
								</th>
								<th scope="col" style={{ textAlign: "left", padding: 8 }}>
									contentType
								</th>
								<th scope="col" style={{ textAlign: "right", padding: 8 }}>
									크기
								</th>
								<th scope="col" style={{ padding: 8 }} aria-label="동작" />
							</tr>
						</thead>
						<tbody>
							{items.map((it, idx) => (
								<tr key={it.documentId} style={{ borderBottom: "1px solid var(--pk-gray-100)" }}>
									<td style={{ padding: 8 }}>{it.file.name}</td>
									<td style={{ padding: 8 }}>
										<input
											value={it.documentId}
											aria-label={`${it.file.name} 의 documentId`}
											onChange={(e) => editItem(idx, { documentId: e.target.value })}
											style={{
												width: "100%",
												padding: 4,
												border: "1px solid var(--pk-gray-300)",
												borderRadius: 4
											}}
										/>
									</td>
									<td style={{ padding: 8 }}>
										<input
											value={it.contentType}
											aria-label={`${it.file.name} 의 contentType`}
											onChange={(e) => editItem(idx, { contentType: e.target.value })}
											style={{
												width: "100%",
												padding: 4,
												border: "1px solid var(--pk-gray-300)",
												borderRadius: 4
											}}
										/>
									</td>
									<td style={{ padding: 8, textAlign: "right" }}>{(it.file.size / 1024).toFixed(1)} KB</td>
									<td style={{ padding: 8 }}>
										<button
											onClick={() => removeItem(idx)}
											type="button"
											className="btn-ghost"
											aria-label={`${it.file.name} 제거`}
										>
											제거
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			<div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
				<button type="button" onClick={runDryRun} disabled={isBusy || items.length === 0} className="btn">
					{busy === "dry-run" ? "미리 추정 중…" : "미리 추정"}
				</button>
				<button
					type="button"
					onClick={runUpload}
					disabled={isBusy || items.length === 0 || !dryRunResult}
					className="btn-primary"
					title={!dryRunResult ? "먼저 '미리 추정' 으로 비용을 확인해 주세요" : undefined}
				>
					{busy === "upload" ? "업로드 중…" : "업로드 시작"}
				</button>
			</div>

			{error && (
				<div
					style={{
						padding: 12,
						marginBottom: 12,
						background: "var(--pk-danger-soft)",
						border: "1px solid var(--pk-danger-border)",
						borderRadius: 6,
						color: "var(--pk-danger)"
					}}
					role="alert"
				>
					{error}
				</div>
			)}

			{dryRunResult && (
				<output
					aria-live="polite"
					style={{
						display: "block",
						padding: 20,
						marginBottom: 12,
						background: "var(--pk-brand-50)",
						borderRadius: 6
					}}
				>
					<h3 style={{ marginTop: 0, marginBottom: 16 }}>추정 결과</h3>
					{/* Designer R2 round2: typography hierarchy — 비용/시간을 큰 숫자로. sticker shock 노출 의도 */}
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
							gap: 16,
							marginBottom: 12
						}}
					>
						<div>
							<div className="muted" style={{ fontSize: 12 }}>
								문서 수
							</div>
							<div style={{ fontSize: 24, fontWeight: 600 }}>{dryRunResult.totalItems}</div>
						</div>
						<div>
							<div className="muted" style={{ fontSize: 12 }}>
								추정 토큰
							</div>
							<div style={{ fontSize: 24, fontWeight: 600 }}>
								{(dryRunResult.estimatedTotalTokens ?? 0).toLocaleString()}
							</div>
						</div>
						<div>
							<div className="muted" style={{ fontSize: 12 }}>
								추정 비용
							</div>
							<div
								style={{
									fontSize: 24,
									fontWeight: 600,
									color: dryRunResult.warningHighCost ? "var(--pk-danger)" : undefined
								}}
							>
								${(dryRunResult.estimatedCostUsd ?? 0).toFixed(4)}
							</div>
						</div>
						<div>
							<div className="muted" style={{ fontSize: 12 }}>
								예상 소요
							</div>
							<div
								style={{
									fontSize: 24,
									fontWeight: 600,
									color: dryRunResult.warningLongDuration ? "var(--pk-warning)" : undefined
								}}
							>
								{Math.round((dryRunResult.estimatedDurationMs ?? 0) / 1000)}초
							</div>
						</div>
					</div>
					{(dryRunResult.warningHighCost || dryRunResult.warningLongDuration) && (
						<div
							style={{
								padding: 10,
								marginBottom: 8,
								background: "var(--pk-warning-soft)",
								border: "1px solid var(--pk-warning-border)",
								borderRadius: 6,
								fontSize: 13,
								color: "var(--pk-warning)"
							}}
							role="alert"
						>
							{dryRunResult.warningHighCost && (
								<div>⚠️ 추정 비용 $1 초과 — 정말 진행할지 다시 확인해 주세요.</div>
							)}
							{dryRunResult.warningLongDuration && (
								<div>⚠️ 예상 소요 60초 초과 — Lambda 15분 한도 위험. 청크 분할 권장.</div>
							)}
						</div>
					)}
					<p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
						※ text-embedding-3-large 평균 토큰/바이트 비율 기준 ±25% 오차. CJK 비중 자동 보정. 실 청구는
						OpenAI metering 따름. LLM 추출은 별도 비용 — 본 추정에 미포함.
					</p>
				</output>
			)}

			{uploadResult && (
				<output
					aria-live="polite"
					style={{
						display: "block",
						padding: 16,
						marginBottom: 12,
						background: "var(--pk-gray-50)",
						borderRadius: 6
					}}
				>
					<h3 style={{ marginTop: 0 }}>업로드 결과</h3>
					<p>
						성공 <strong style={{ color: "var(--pk-success)" }}>✓ {uploadResult.successCount}</strong> / 실패{" "}
						<strong style={{ color: uploadResult.failureCount > 0 ? "var(--pk-danger)" : "var(--pk-muted)" }}>
							✗ {uploadResult.failureCount}
						</strong>{" "}
						/ 총 {uploadResult.totalItems}
					</p>
					<ul style={{ paddingLeft: 20 }}>
						{uploadResult.items.map((it) => (
							<li key={it.documentId} style={{ color: it.ok ? "var(--pk-success)" : "var(--pk-danger)" }}>
								<span aria-hidden="true">{it.ok ? "✓ " : "✗ "}</span>
								<code>{it.documentId}</code>{" "}
								{it.ok
									? `— ${it.created ? "created" : "unchanged"} (${it.version?.version.slice(0, 19)})`
									: `— ${it.errorCode}: ${it.errorMessage}`}
							</li>
						))}
					</ul>
				</output>
			)}
		</div>
	)
}
